/**
 * The machine-owned blocks inside a model's `notes` field, and what a person wrote.
 *
 * `notes` is one string on the model document, and two writers share it. A person types
 * into it from this page, and rgip-copilot merges delimited blocks into it: what the
 * model is being FRAME'd for, a BUILD_LOG line per write it made during a build, and the
 * DECISIONS a person approved for this model in Memory (rgip ADR 0027).
 * The markers are HTML comments, so today they are read aloud to the person in a plain
 * textarea:
 *
 *     <!-- rgip:build-log 2026-09-02 -->
 *     tree 3 node 12: employees >= 500, over industry = software. Mid-market converts 3x.
 *     <!-- /rgip:build-log -->
 *
 * This is the reading half of `app/sources/springbok/notes.py` in rgip-copilot, and the
 * two have to agree on the format or one of them eats the other's text. The pairing rule
 * is copied from it deliberately: **the LAST opening marker, paired with the FIRST closing
 * marker after it.** An unpaired marker is not a block. It stays in the person's text,
 * where a half-deleted block belongs, rather than swallowing whatever was written after it.
 *
 * Reading only. Nothing here writes a block: `withHumanNotes` puts the ones it found back
 * verbatim, which is the whole of this module's contract with rgip.
 */

const FRAME = 'model-frame';
const BUILD_LOG = 'build-log';
const DECISIONS = 'decisions';

/** Every block rgip owns. One missing here is one that leaks into the person's text. */
const BLOCKS = [FRAME, BUILD_LOG, DECISIONS] as const;
type BlockName = (typeof BLOCKS)[number];

interface NotesBlock {
  name: BlockName;
  /** Whatever the opening marker carries after the name, normally the date it was written. */
  stamp: string;
  /** What sits between the markers. */
  body: string;
  /** The block including both markers, byte for byte as stored. */
  raw: string;
  start: number;
  end: number;
}

const opener = (name: BlockName) =>
  new RegExp(`<!--\\s*rgip:${name}\\b([^>]*)-->`, 'g');

const closer = (name: BlockName) => `<!-- /rgip:${name} -->`;

/** The named block, or null when there is none and when there is only half of one. */
function readBlock(
  notes: string | null | undefined,
  name: BlockName
): NotesBlock | null {
  if (!notes) return null;
  const pattern = opener(name);
  let last: RegExpExecArray | null = null;
  let found = pattern.exec(notes);
  while (found !== null) {
    last = found;
    found = pattern.exec(notes);
  }
  if (!last) return null;
  const bodyStart = last.index + last[0].length;
  const close = closer(name);
  const bodyEnd = notes.indexOf(close, bodyStart);
  if (bodyEnd === -1) return null;
  const end = bodyEnd + close.length;
  return {
    name,
    stamp: (last[1] || '').trim(),
    body: notes.slice(bodyStart, bodyEnd).trim(),
    raw: notes.slice(last.index, end),
    start: last.index,
    end,
  };
}

/** What rgip stored as the model's frame, or null. An emptied block is not a frame. */
export function readFrame(notes: string | null | undefined): NotesBlock | null {
  const block = readBlock(notes, FRAME);
  return block && block.body ? block : null;
}

export interface BuildLogEntry {
  /** The whole line exactly as stored, shas and all. Not what is rendered. */
  raw: string;
  /** `tree 3 node 12` when the line opens with one, for emphasis. Never invented. */
  subject: string;
  /** The rest of the line, with any commit sha shortened. What is rendered. */
  text: string;
}

export interface BuildLog {
  /** Oldest first, the order rgip appends in. */
  entries: BuildLogEntry[];
  /** How many older entries rgip dropped to stay inside its own cap. */
  trimmed: number;
  stamp: string;
}

// rgip's own marker for what its 30-entry cap threw away (`notes.py: _TRIMMED`). It is
// not an entry, and counting it as one would put "... 4 earlier entries not kept" in the
// list as though somebody had decided it.
const TRIMMED = /^\.\.\. (\d+) earlier entries not kept$/;

// The one prefix `_log_entry` is guaranteed to write. Anything else falls through with an
// empty subject rather than being cut at a guess: a condition can contain a colon, and a
// wrong split would put half a threshold in the heading.
const SUBJECT = /^(tree \d+ node \d+):\s*/;

// A git commit sha, written into the log by `springbok_restore_tree`, which composes its
// own entry rather than going through `_log_entry`. rgip has a rule against this and
// applies it one function away: `tree_versions` hands back a `step_back` position instead
// of a sha, because "whatever reaches the caller reaches the customer" and a 40-character
// hash in a sentence is plumbing wearing the costume of an answer. The log is the more
// human-facing of the two and missed the rule.
//
// Shortened here rather than hidden: the sha is the handle on a restore and a reader
// comparing against the tree's history needs its first few characters. Fixing the writer
// is an rgip change; this makes every entry already stored readable too, and there are
// 30 of them per model that no fix upstream will rewrite.
const SHA = /\b[0-9a-f]{40}\b/g;

const shorten = (line: string) => line.replace(SHA, (sha) => sha.slice(0, 7));

/** The build log rgip wrote into these notes, or null when there is none. */
export function readBuildLog(
  notes: string | null | undefined
): BuildLog | null {
  const block = readBlock(notes, BUILD_LOG);
  if (!block || !block.body) return null;
  const lines = block.body
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
  let trimmed = 0;
  const entries: BuildLogEntry[] = [];
  lines.forEach((raw) => {
    const dropped = TRIMMED.exec(raw);
    if (dropped) {
      trimmed = Number(dropped[1]);
      return;
    }
    const subject = SUBJECT.exec(raw);
    const rest = subject ? raw.slice(subject[0].length) : raw;
    entries.push({
      raw,
      subject: subject ? subject[1] : '',
      text: shorten(rest),
    });
  });
  return { entries, trimmed, stamp: block.stamp };
}

export interface Decision {
  /** The whole line exactly as stored. */
  raw: string;
  /** `2026-10-14`, the day it was approved, when the line opens with one. */
  date: string;
  /** The decision itself, `text (instead of X; why: Y)` as rgip renders it. */
  text: string;
}

export interface Decisions {
  /** In the order rgip wrote them. */
  entries: Decision[];
  stamp: string;
}

// `render.notes_block_body` writes `<date>  <entry_line>`, two spaces apart. A line that
// does not open with a date is kept whole as the text rather than cut at a guess.
const DATED = /^(\d{4}-\d{2}-\d{2})\s+(.*)$/;

/**
 * The decisions block: what a person approved for this model, or null when there is none.
 *
 * rgip rebuilds this block whole from its memory store on every change and writes it with
 * its markers even when nothing is left, so an empty block is "no decisions", not a fault.
 */
export function readDecisions(
  notes: string | null | undefined
): Decisions | null {
  const block = readBlock(notes, DECISIONS);
  if (!block || !block.body) return null;
  const entries = block.body
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((raw) => {
      const dated = DATED.exec(raw);
      return {
        raw,
        date: dated ? dated[1] : '',
        text: dated ? dated[2] : raw,
      };
    });
  return { entries, stamp: block.stamp };
}

/**
 * `notes` with every block lifted out: what a person actually wrote.
 *
 * This is what the read view renders and what the editor loads, so a block missing from
 * `BLOCKS` would be handed to them as their own prose and saved back as such.
 */
export function humanNotes(notes: string | null | undefined): string {
  if (!notes) return '';
  let remainder = notes;
  BLOCKS.forEach((name) => {
    const block = readBlock(remainder, name);
    if (!block) return;
    remainder = `${remainder
      .slice(0, block.start)
      .replace(/\s+$/, '')}\n\n${remainder
      .slice(block.end)
      .replace(/^\s+/, '')}`;
  });
  return remainder.trim();
}

/**
 * The whole `notes` value to save, with the person's text replaced and rgip's blocks kept.
 *
 * The save route replaces the field whole, so an editor holding only the human half has to
 * put the other half back or the build log is deleted by somebody adding a sentence. Each
 * block goes back as its own `raw` string, markers and date included, so nothing this
 * module does not understand is reformatted on the way through.
 *
 * Blocks are appended BELOW the prose, in the order they appeared, even when one of them
 * was originally in the middle of it. Deliberate: a block is machine text a person cannot
 * edit, and the alternative is preserving interleaving that only exists because rgip
 * appended at a moment when the notes ended there.
 */
export function withHumanNotes(
  notes: string | null | undefined,
  human: string
): string {
  const blocks = BLOCKS.map((name) => readBlock(notes, name))
    .filter((block): block is NotesBlock => block !== null)
    .sort((a, b) => a.start - b.start);
  return [human.trim(), ...blocks.map((block) => block.raw)]
    .filter(Boolean)
    .join('\n\n');
}
